import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useAuth } from "./../../hooks/auth";
import api from "./../../services/api";
import { takePendingPropertyAlert } from "./../../utils/propertyAlertPending";
import { hasOwnerScopedAccess } from "../../utils/canManageSubLogins";

import Header from "./../../components/Header/";
import Footer from "./../../components/Footer/";
import HouseCardToFavoritePage from "./../../components/HouseCardToFavoritePage/";
import PropertyAlertEditModal from "./PropertyAlertEditModal";
import PropertyAlertsSection, {
  PropertyAlertListItem,
} from "./PropertyAlertsSection";

import {
  Container,
  Content,
  InitialContainerToMobile,
  TitleAndImageContainer,
  NoticeBanner,
  NoticeClose,
  NoticeStrip,
  PageWithTabsLayout,
  TabList,
  TabButton,
  TabPanel,
} from "./styles";

import favoritesImg from "./../../assets/pages/FavoriteProperties/favoritesImg.svg";

const NOTICE_KEYS = {
  saved: "propertyAlertSaved",
} as const;

type BannerVariant = "success" | "info" | "error";

type ActiveTab = "alerts" | "favorites";

function normalizeAlertsPayload(data: unknown): PropertyAlertListItem[] {
  let raw: unknown = (data as { data?: unknown })?.data ?? data;
  if (
    raw &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    Array.isArray((raw as { data?: unknown }).data)
  ) {
    raw = (raw as { data: PropertyAlertListItem[] }).data;
  }
  return Array.isArray(raw) ? raw : [];
}

export default function ScheduledVisits() {
  const { user, access_token } = useAuth();
  const history = useHistory();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState<ActiveTab>("favorites");
  const [alerts, setAlerts] = useState<PropertyAlertListItem[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [removingAlertId, setRemovingAlertId] = useState<number | null>(null);
  const [editingAlert, setEditingAlert] = useState<PropertyAlertListItem | null>(
    null
  );
  const [flash, setFlash] = useState<{
    variant: BannerVariant;
    text: string;
  } | null>(null);

  const notice = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("notice");
  }, [location.search]);

  const clearNoticeFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("notice");
    const q = params.toString();
    history.replace({
      pathname: location.pathname,
      search: q ? `?${q}` : "",
    });
  }, [history, location.pathname, location.search]);

  const dismissBanner = useCallback(() => {
    setFlash(null);
    clearNoticeFromUrl();
  }, [clearNoticeFromUrl]);

  const urlNoticeBanner = useMemo(() => {
    if (notice === NOTICE_KEYS.saved) {
      return {
        variant: "success" as const,
        text: "Alerta de imóveis salvo com sucesso.",
      };
    }
    return null;
  }, [notice]);

  const topBanner = flash ?? urlNoticeBanner;

  const loadAlerts = useCallback(async () => {
    if (!access_token) return;
    setAlertsLoading(true);
    try {
      api.defaults.headers.authorization = `Bearer ${access_token}`;
      const { data } = await api.get("/api/client/tenant/property-alerts");
      setAlerts(normalizeAlertsPayload(data));
    } catch {
      setAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  }, [access_token]);

  useEffect(() => {
    if (notice === NOTICE_KEYS.saved) {
      setActiveTab("alerts");
    }
  }, [notice]);

  useEffect(() => {
    if (!user || !access_token) return;

    let cancelled = false;

    (async () => {
      const pending = takePendingPropertyAlert();
      if (pending) {
        try {
          api.defaults.headers.authorization = `Bearer ${access_token}`;
          await api.post("/api/client/tenant/property-alerts", pending);
          if (!cancelled) {
            setFlash({
              variant: "success",
              text: "Login realizado e alerta salvo com sucesso.",
            });
            setActiveTab("alerts");
            history.replace({ pathname: "/favorites" });
          }
        } catch (e: unknown) {
          const ax = e as { response?: { data?: { message?: string } } };
          const msg =
            ax.response?.data?.message ||
            "Não foi possível salvar o alerta após o login. Crie o alerta novamente na busca de imóveis.";
          if (!cancelled) {
            setFlash({ variant: "error", text: msg });
            history.replace({ pathname: "/favorites" });
          }
        }
      }

      if (cancelled) return;
      await loadAlerts();
    })();

    return () => {
      cancelled = true;
    };
  }, [user, access_token, history, loadAlerts]);

  const handleRemoveAlert = useCallback(
    async (id: number) => {
      if (!access_token) return;
      setRemovingAlertId(id);
      try {
        api.defaults.headers.authorization = `Bearer ${access_token}`;
        await api.delete(`/api/client/tenant/property-alerts/${id}`);
        setAlerts((prev) => prev.filter((a) => a.id !== id));
      } catch {
        setFlash({
          variant: "error",
          text: "Não foi possível excluir o alerta. Tente novamente.",
        });
      } finally {
        setRemovingAlertId(null);
      }
    },
    [access_token]
  );

  const handleAlertSaved = useCallback(() => {
    void loadAlerts();
    setFlash({
      variant: "success",
      text: "Alerta atualizado com sucesso.",
    });
  }, [loadAlerts]);

  const userRoleLabel = hasOwnerScopedAccess(user)
    ? "PROPRIETÁRIO"
    : "INQUILINO";

  return (
    <Container>
      <Header
        hasBorder={window.innerWidth > 767 ? true : false}
        hasSearchBar
        hasOptionToSearch
      />

      {topBanner && (
        <NoticeStrip>
          <NoticeBanner $variant={topBanner.variant} role="status">
            {topBanner.text}
            <NoticeClose
              type="button"
              onClick={dismissBanner}
              aria-label="Fechar aviso"
            >
              ×
            </NoticeClose>
          </NoticeBanner>
        </NoticeStrip>
      )}

      <Content>
        <InitialContainerToMobile>
          {!!user && <h3>{userRoleLabel}</h3>}
          <h1>Favoritos e alertas</h1>

          <img src={favoritesImg} alt="" />
        </InitialContainerToMobile>

        <PageWithTabsLayout>
          <TabList role="tablist" aria-label="Favoritos e alertas de imóveis">
            <TabButton
              type="button"
              role="tab"
              id="tab-favorites-properties"
              aria-selected={activeTab === "favorites"}
              aria-controls="panel-favorites-properties"
              $active={activeTab === "favorites"}
              onClick={() => setActiveTab("favorites")}
            >
              Favoritos
            </TabButton>
            <TabButton
              type="button"
              role="tab"
              id="tab-favorites-alerts"
              aria-selected={activeTab === "alerts"}
              aria-controls="panel-favorites-alerts"
              $active={activeTab === "alerts"}
              onClick={() => setActiveTab("alerts")}
            >
              Alertas de imóveis
            </TabButton>
          </TabList>

          <TabPanel
            role="tabpanel"
            id="panel-favorites-properties"
            aria-labelledby="tab-favorites-properties"
            aria-hidden={activeTab !== "favorites"}
            $visible={activeTab === "favorites"}
          >
            <HouseCardToFavoritePage />
          </TabPanel>

          <TabPanel
            role="tabpanel"
            id="panel-favorites-alerts"
            aria-labelledby="tab-favorites-alerts"
            aria-hidden={activeTab !== "alerts"}
            $visible={activeTab === "alerts"}
          >
            <PropertyAlertsSection
              loading={alertsLoading}
              alerts={alerts}
              removingId={removingAlertId}
              onRemove={handleRemoveAlert}
              onEdit={setEditingAlert}
            />
          </TabPanel>
        </PageWithTabsLayout>

        {activeTab === "favorites" && (
          <TitleAndImageContainer>
            {!!user && <h4>{userRoleLabel}</h4>}

            <h1>Imóveis Favoritos</h1>

            <img src={favoritesImg} alt="" />
          </TitleAndImageContainer>
        )}
      </Content>

      {access_token && (
        <PropertyAlertEditModal
          open={!!editingAlert}
          onClose={() => setEditingAlert(null)}
          alert={editingAlert}
          accessToken={access_token}
          onSaved={handleAlertSaved}
        />
      )}

      <Footer />
    </Container>
  );
}
